// src/components/admin/AdminUsers.jsx
// Full user management — mobile-responsive

import { useState, useEffect, useCallback, useRef } from 'react';
import { adminApi } from '../../utils/api';

// ── Helpers ───────────────────────────────────────────────────
const initials = n => n?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '??';
const fmtDate  = d => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const fmtTime  = d => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const AVATAR_COLORS = ['var(--gold)', 'var(--blue)', 'var(--green)', '#f472b6', '#a78bfa'];

function Tag({ text, bg, color }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 9px', borderRadius: 20, fontSize: 10, fontWeight: 700, letterSpacing: '.04em', background: bg, color }}>
      {text}
    </span>
  );
}

function Spinner() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60, color: 'var(--gray)', fontSize: 13 }}>
      Loading…
    </div>
  );
}

// ── Responsive styles ─────────────────────────────────────────
const MOBILE_CSS = `
@media (max-width: 768px) {
  .users-table-wrap { display: none !important; }
  .users-card-list  { display: flex !important; flex-direction: column; gap: 8px; }
  .users-filter-bar { flex-direction: column !important; align-items: stretch !important; gap: 8px !important; }
  .users-filter-tabs { overflow-x: auto; -webkit-overflow-scrolling: touch; white-space: nowrap; scrollbar-width: none; }
  .users-filter-tabs::-webkit-scrollbar { display: none; }
  .users-search { width: 100% !important; margin-left: 0 !important; }
  .udetail-stats-grid { grid-template-columns: repeat(2, 1fr) !important; }
  .udetail-footer { flex-wrap: wrap !important; }
  .udetail-footer button { flex: 1 1 calc(50% - 4px) !important; min-width: 120px; }
  /* Modal as bottom-sheet on mobile */
  .udetail-modal-inner {
    position: fixed !important;
    bottom: 0 !important;
    left: 0 !important;
    right: 0 !important;
    max-width: 100% !important;
    max-height: 88vh !important;
    border-radius: var(--radius) var(--radius) 0 0 !important;
  }
}
@media (min-width: 769px) {
  .users-card-list { display: none !important; }
}
@media (max-width: 400px) {
  .udetail-stats-grid { grid-template-columns: 1fr !important; }
  .udetail-footer button { flex: 1 1 100% !important; }
}
`;

function InjectStyle({ id, css }) {
  useEffect(() => {
    if (document.getElementById(id)) return;
    const el = document.createElement('style');
    el.id = id;
    el.textContent = css;
    document.head.appendChild(el);
  }, []);
  return null;
}

// ── Confirm Dialog ────────────────────────────────────────────
function ConfirmDialog({ message, danger, onConfirm, onCancel }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,.75)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'var(--dark2)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 'var(--radius)', padding: '24px 22px', maxWidth: 380, width: '100%' }}>
        <div style={{ fontSize: 14, color: 'var(--off-white)', lineHeight: 1.7, marginBottom: 20 }}>{message}</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onConfirm} style={{ flex: 1, padding: '10px 0', background: danger ? 'rgba(217,79,79,.15)' : 'rgba(212,168,67,.15)', border: `1px solid ${danger ? 'rgba(217,79,79,.4)' : 'rgba(212,168,67,.4)'}`, color: danger ? 'var(--red)' : 'var(--gold)', fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: "'Inter',sans-serif", borderRadius: 'var(--radius)' }}>Confirm</button>
          <button onClick={onCancel}  style={{ flex: 1, padding: '10px 0', background: 'transparent', border: '1px solid rgba(255,255,255,.1)', color: 'var(--gray)', fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: "'Inter',sans-serif", borderRadius: 'var(--radius)' }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── User Detail Modal ─────────────────────────────────────────
function UserDetailModal({ userId, onClose, onSuspendToggle, onDelete, onRoleChange }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting,  setActing]  = useState(false);
  const [confirm, setConfirm] = useState(null);

  useEffect(() => {
    adminApi.getUserDetail(userId)
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId]);

  const handleSuspend = async () => {
    const isSuspended = data?.user?.suspended;
    setConfirm({
      message: isSuspended
        ? `Reactivate ${data.user.name}'s account? They will be able to log in again.`
        : `Suspend ${data.user.name}'s account? They will be logged out immediately and unable to sign in.`,
      danger: !isSuspended,
      onConfirm: async () => {
        setConfirm(null);
        setActing(true);
        try {
          const res = await adminApi.suspendUser(userId);
          setData(d => ({ ...d, user: { ...d.user, suspended: res.suspended } }));
          onSuspendToggle(userId, res.suspended);
        } finally { setActing(false); }
      },
    });
  };

  const handleDelete = () => {
    setConfirm({
      message: `Permanently delete ${data?.user?.name}'s account? This cannot be undone. All trades, notes, and data will be erased.`,
      danger: true,
      onConfirm: async () => {
        setConfirm(null);
        setActing(true);
        try {
          await adminApi.deleteUser(userId);
          onDelete(userId);
          onClose();
        } finally { setActing(false); }
      },
    });
  };

  const handleRoleChange = () => {
    const isAdmin   = data?.user?.role === 'admin';
    const newRole   = isAdmin ? 'user' : 'admin';
    const name      = data?.user?.name;
    setConfirm({
      message: isAdmin
        ? `Revoke admin privileges from ${name}? They will become a regular user and lose access to this panel.`
        : `Grant admin privileges to ${name}? They will have full access to this admin panel.`,
      danger: !isAdmin,
      onConfirm: async () => {
        setConfirm(null);
        setActing(true);
        try {
          const res = await adminApi.setUserRole(userId, newRole);
          setData(d => ({ ...d, user: { ...d.user, role: res.role } }));
          onRoleChange(userId, res.role);
        } finally { setActing(false); }
      },
    });
  };

  const parseUA = ua => {
    if (!ua) return 'Unknown';
    if (/mobile|android|iphone/i.test(ua)) return '📱 Mobile';
    if (/ipad|tablet/i.test(ua)) return '📱 Tablet';
    return '🖥 Desktop';
  };

  const u           = data?.user;
  const isSuspended = u?.suspended;
  const isAdmin     = u?.role === 'admin';

  return (<>
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(0,0,0,.75)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={onClose}
    >
      <div
        style={{ background: 'var(--dark2)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 'var(--radius)', width: '100%', maxWidth: 640, maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative' }}
        className="udetail-modal-inner"
        onClick={e => e.stopPropagation()}
      >
        <div style={{ height: 1, background: 'linear-gradient(90deg, var(--gold), transparent 60%)', flexShrink: 0 }}/>

        {/* Header */}
        <div style={{ padding: '16px 18px', borderBottom: '1px solid rgba(255,255,255,.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--white)' }}>User Detail</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--gray)', cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: '0 4px' }}>×</button>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '18px 18px' }}>
          {loading ? <Spinner/> : !u ? (
            <div style={{ color: 'var(--red)', fontSize: 13 }}>Failed to load user.</div>
          ) : (
            <>
              {/* Identity */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
                <div style={{ width: 48, height: 48, borderRadius: 8, background: 'var(--gold)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 800, color: 'var(--black)', flexShrink: 0 }}>
                  {initials(u.name)}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--white)' }}>{u.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--gray)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                    <Tag text={u.role.toUpperCase()} bg="rgba(212,168,67,.12)" color="var(--gold)"/>
                    <Tag
                      text={u.referralReceived?.status === 'ACTIVE' ? 'PAID' : 'FREE'}
                      bg={u.referralReceived?.status === 'ACTIVE' ? 'rgba(46,204,138,.12)' : 'rgba(148,163,184,.1)'}
                      color={u.referralReceived?.status === 'ACTIVE' ? 'var(--green)' : 'var(--gray)'}
                    />
                    {isSuspended && <Tag text="SUSPENDED" bg="rgba(217,79,79,.12)" color="var(--red)"/>}
                  </div>
                </div>
              </div>

              {/* Stats grid */}
              <div className="udetail-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 18 }}>
                {[
                  ['Joined',        fmtDate(u.createdAt)],
                  ['Trades Logged', data.tradeCount ?? 0],
                  ['Account Type',  u.profile?.accType || '—'],
                  ['Level',         u.profile?.level || '—'],
                  ['Timezone',      u.profile?.timezone || '—'],
                  ['Plan',          u.referralReceived?.planName || 'Free'],
                ].map(([l, v]) => (
                  <div key={l} style={{ background: 'var(--dark3)', padding: '10px 12px', borderRadius: 'var(--radius)' }}>
                    <div style={{ fontSize: 10, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--gray)', marginBottom: 4, fontWeight: 600 }}>{l}</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--off-white)' }}>{v}</div>
                  </div>
                ))}
              </div>

              {/* Sessions */}
              {data.sessions?.length > 0 && (
                <div style={{ marginBottom: 18 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--gray)', marginBottom: 10 }}>Recent Sessions</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {data.sessions.slice(0, 5).map(s => (
                      <div key={s.id} style={{ background: 'var(--dark3)', padding: '10px 12px', borderRadius: 'var(--radius)', display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 14 }}>{parseUA(s.userAgent).split(' ')[0]}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, color: 'var(--off-white)', fontWeight: 500 }}>{parseUA(s.userAgent).split(' ').slice(1).join(' ') || 'Unknown device'}</div>
                          <div style={{ fontSize: 11, color: 'var(--gray)', marginTop: 2 }}>
                            {s.city && s.country ? `${s.city}, ${s.country} · ` : ''}{s.ipAddress || '—'}
                          </div>
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--gray)', textAlign: 'right', flexShrink: 0 }}>
                          <div>{fmtTime(s.createdAt)}</div>
                          <div style={{ marginTop: 2, color: new Date(s.expiresAt) > new Date() ? 'var(--green)' : 'var(--gray)' }}>
                            {new Date(s.expiresAt) > new Date() ? 'Active' : 'Expired'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Actions footer */}
        {!loading && u && (
          <div className="udetail-footer" style={{ padding: '14px 18px', borderTop: '1px solid rgba(255,255,255,.06)', display: 'flex', gap: 8, flexShrink: 0 }}>
            <button onClick={handleSuspend} disabled={acting} style={{ flex: 1, padding: '10px 0', background: isSuspended ? 'rgba(46,204,138,.1)' : 'rgba(251,191,36,.1)', border: `1px solid ${isSuspended ? 'rgba(46,204,138,.3)' : 'rgba(251,191,36,.3)'}`, color: isSuspended ? 'var(--green)' : 'var(--warning)', fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', cursor: acting ? 'not-allowed' : 'pointer', fontFamily: "'Inter',sans-serif", borderRadius: 'var(--radius)', opacity: acting ? .5 : 1 }}>
              {isSuspended ? '✓ Reactivate' : '⊘ Suspend'}
            </button>
            <button
              onClick={handleRoleChange}
              disabled={acting}
              title={isAdmin ? 'Revoke admin privileges' : 'Promote to admin'}
              style={{ padding: '10px 14px', background: isAdmin ? 'rgba(148,163,184,.08)' : 'rgba(212,168,67,.1)', border: `1px solid ${isAdmin ? 'rgba(148,163,184,.2)' : 'rgba(212,168,67,.3)'}`, color: isAdmin ? 'var(--gray)' : 'var(--gold)', fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', cursor: acting ? 'not-allowed' : 'pointer', fontFamily: "'Inter',sans-serif", borderRadius: 'var(--radius)', opacity: acting ? .5 : 1, whiteSpace: 'nowrap' }}>
              {isAdmin ? '⬇ Demote' : '⬆ Make Admin'}
            </button>
            <button onClick={handleDelete} disabled={acting} style={{ padding: '10px 18px', background: 'rgba(217,79,79,.1)', border: '1px solid rgba(217,79,79,.3)', color: 'var(--red)', fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', cursor: acting ? 'not-allowed' : 'pointer', fontFamily: "'Inter',sans-serif", borderRadius: 'var(--radius)', opacity: acting ? .5 : 1 }}>
              🗑 Delete
            </button>
          </div>
        )}
      </div>
    </div>
    {confirm && <ConfirmDialog {...confirm} onCancel={() => setConfirm(null)}/>}
  </>);
}

// ── User Card (mobile list) ───────────────────────────────────
function UserCard({ u, i, onView, onQuickSuspend }) {
  return (
    <div
      onClick={() => onView(u.id)}
      style={{ background: 'var(--dark2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '14px 14px', display: 'flex', gap: 12, cursor: 'pointer', opacity: u.suspended ? .7 : 1 }}
    >
      <div style={{ width: 36, height: 36, borderRadius: 7, flexShrink: 0, background: AVATAR_COLORS[i % AVATAR_COLORS.length], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, color: 'var(--black)' }}>
        {initials(u.name)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--white)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</div>
        <div style={{ fontSize: 11, color: 'var(--gray)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 1 }}>{u.email}</div>
        <div style={{ display: 'flex', gap: 6, marginTop: 7, flexWrap: 'wrap', alignItems: 'center' }}>
          <Tag text={u.plan?.toUpperCase() || 'FREE'} bg={u.plan === 'paid' ? 'rgba(46,204,138,.12)' : 'rgba(148,163,184,.1)'} color={u.plan === 'paid' ? 'var(--green)' : 'var(--gray)'}/>
          {u.suspended
            ? <Tag text="SUSPENDED" bg="rgba(217,79,79,.12)" color="var(--red)"/>
            : <Tag text="ACTIVE"    bg="rgba(46,204,138,.08)" color="var(--green)"/>
          }
          <span style={{ fontSize: 10, color: 'var(--gray)', marginLeft: 'auto' }}>{fmtDate(u.joinedAt)}</span>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flexShrink: 0 }} onClick={e => e.stopPropagation()}>
        <button
          onClick={e => onQuickSuspend(e, u)}
          style={{ padding: '6px 10px', background: u.suspended ? 'rgba(46,204,138,.08)' : 'rgba(251,191,36,.08)', border: `1px solid ${u.suspended ? 'rgba(46,204,138,.25)' : 'rgba(251,191,36,.25)'}`, color: u.suspended ? 'var(--green)' : 'var(--warning)', fontSize: 10, fontWeight: 700, letterSpacing: '.04em', cursor: 'pointer', fontFamily: "'Inter',sans-serif", borderRadius: 4, whiteSpace: 'nowrap' }}
        >
          {u.suspended ? 'RESTORE' : 'SUSPEND'}
        </button>
      </div>
    </div>
  );
}

// ── Main AdminUsers page ──────────────────────────────────────
export default function AdminUsers() {
  const [data,        setData]        = useState({ users: [], total: 0, pages: 1 });
  const [filter,      setFilter]      = useState('all');
  const [status,      setStatus]      = useState('all');
  const [search,      setSearch]      = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page,        setPage]        = useState(1);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [detailId,    setDetailId]    = useState(null);
  const [confirm,     setConfirm]     = useState(null);
  const searchTimer = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    adminApi.getUsers({
      search,
      plan:      filter,
      suspended: status === 'all' ? undefined : status === 'suspended' ? 'true' : 'false',
      page,
      limit: 50,
    })
      .then(d  => setData(d))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [search, filter, status, page]);

  useEffect(() => { load(); }, [load]);

  const handleSearchChange = e => {
    setSearchInput(e.target.value);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearch(e.target.value);
      setPage(1);
    }, 350);
  };

  const handleSuspendToggle = (id, suspended) => {
    setData(d => ({
      ...d,
      users: d.users.map(u => u.id === id ? { ...u, suspended } : u),
    }));
  };

  const handleDelete = id => {
    setData(d => ({ ...d, users: d.users.filter(u => u.id !== id), total: d.total - 1 }));
  };

  const handleRoleChange = (id, role) => {
    setData(d => ({ ...d, users: d.users.map(u => u.id === id ? { ...u, role } : u) }));
  };

  const quickSuspend = (e, u) => {
    e.stopPropagation();
    setConfirm({
      message: u.suspended
        ? `Reactivate ${u.name}'s account?`
        : `Suspend ${u.name}? They will be logged out immediately.`,
      danger: !u.suspended,
      onConfirm: async () => {
        setConfirm(null);
        try {
          const res = await adminApi.suspendUser(u.id);
          handleSuspendToggle(u.id, res.suspended);
        } catch {}
      },
    });
  };

  const totalActive    = data.users.filter(u => !u.suspended).length;
  const totalSuspended = data.users.filter(u =>  u.suspended).length;
  const totalPaid      = data.users.filter(u =>  u.plan === 'paid').length;

  return (
    <>
      <InjectStyle id="users-mobile-css" css={MOBILE_CSS}/>
      <div style={{ padding: '14px 14px', animation: 'pageIn .3s ease', display: 'flex', flexDirection: 'column', gap: 10 }}>

        {/* Stats strip */}
        <div className="stats-strip">
          {[
            { l: 'Total',     v: data.total },
            { l: 'Active',    v: totalActive,    c: 'var(--green)' },
            { l: 'Suspended', v: totalSuspended, c: 'var(--red)'   },
            { l: 'Paid',      v: totalPaid,      c: 'var(--gold)'  },
          ].map(({ l, v, c }) => (
            <div key={l} className="stats-strip-chip">
              <span className="stats-strip-label">{l}</span>
              <span style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 18, color: c || 'var(--white)', lineHeight: 1 }}>{v}</span>
            </div>
          ))}
        </div>

        {/* Filter bar */}
        <div className="users-filter-bar filter-bar" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div className="users-filter-tabs" style={{ display: 'flex' }}>
            {[['all','All'],['paid','Paid'],['free','Free']].map(([v, l]) => (
              <button key={v} onClick={() => { setFilter(v); setPage(1); }} style={{ padding: '0 14px', height: 38, background: filter === v ? 'rgba(212,168,67,.1)' : 'transparent', border: 'none', borderRight: '1px solid rgba(255,255,255,.04)', color: filter === v ? 'var(--gold)' : 'rgba(247,243,234,.55)', fontSize: 12, fontWeight: filter === v ? 700 : 500, letterSpacing: '.03em', cursor: 'pointer', fontFamily: "'Inter',sans-serif", transition: 'all .15s', whiteSpace: 'nowrap' }}>{l}</button>
            ))}
            <div style={{ width: 1, background: 'rgba(255,255,255,.08)', margin: '6px 4px' }}/>
            {[['all','All Status'],['active','Active'],['suspended','Suspended']].map(([v, l]) => (
              <button key={v} onClick={() => { setStatus(v); setPage(1); }} style={{ padding: '0 14px', height: 38, background: status === v ? 'rgba(212,168,67,.1)' : 'transparent', border: 'none', borderRight: '1px solid rgba(255,255,255,.04)', color: status === v ? 'var(--gold)' : 'rgba(247,243,234,.55)', fontSize: 12, fontWeight: status === v ? 700 : 500, letterSpacing: '.03em', cursor: 'pointer', fontFamily: "'Inter',sans-serif", transition: 'all .15s', whiteSpace: 'nowrap' }}>{l}</button>
            ))}
          </div>
          <input
            className="users-search"
            value={searchInput}
            onChange={handleSearchChange}
            placeholder="Search name or email…"
            style={{ background: 'var(--dark3)', border: '1px solid rgba(255,255,255,.08)', color: 'var(--white)', fontSize: 12, padding: '7px 12px', outline: 'none', fontFamily: "'Inter',sans-serif", width: 200, borderRadius: 'var(--radius)', marginLeft: 'auto' }}
          />
        </div>

        {error && <div style={{ padding: '10px 14px', background: 'rgba(217,79,79,.1)', color: 'var(--red)', fontSize: 13, borderRadius: 'var(--radius)' }}>{error}</div>}

        {loading ? <Spinner/> : (
          <>
            {/* Desktop table */}
            <div className="users-table-wrap tbl-desktop" style={{ height: 'auto', flex: 'unset', overflow: 'auto', maxHeight: '60vh' }}>
              <table>
                <thead>
                  <tr>
                    {['User', 'Email', 'Plan', 'Status', 'Timezone', 'Joined', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '10px 14px', fontSize: 11, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--gray)', fontWeight: 600, background: 'var(--dark3)', position: 'sticky', top: 0, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.users.map((u, i) => (
                    <tr key={u.id} className="tbl-tr" onClick={() => setDetailId(u.id)} style={{ cursor: 'pointer', opacity: u.suspended ? .6 : 1 }}>
                      <td style={{ padding: '11px 14px', borderBottom: '1px solid rgba(255,255,255,.04)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 30, height: 30, borderRadius: 6, flexShrink: 0, background: AVATAR_COLORS[i % AVATAR_COLORS.length], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, color: 'var(--black)' }}>{initials(u.name)}</div>
                          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--white)' }}>{u.name}</span>
                        </div>
                      </td>
                      <td style={{ padding: '11px 14px', fontSize: 12, color: 'var(--gray)', borderBottom: '1px solid rgba(255,255,255,.04)' }}>{u.email}</td>
                      <td style={{ padding: '11px 14px', borderBottom: '1px solid rgba(255,255,255,.04)' }}>
                        <Tag text={u.plan?.toUpperCase() || 'FREE'} bg={u.plan === 'paid' ? 'rgba(46,204,138,.12)' : 'rgba(148,163,184,.1)'} color={u.plan === 'paid' ? 'var(--green)' : 'var(--gray)'}/>
                      </td>
                      <td style={{ padding: '11px 14px', borderBottom: '1px solid rgba(255,255,255,.04)' }}>
                        {u.suspended
                          ? <Tag text="SUSPENDED" bg="rgba(217,79,79,.12)" color="var(--red)"/>
                          : <Tag text="ACTIVE"    bg="rgba(46,204,138,.08)" color="var(--green)"/>
                        }
                      </td>
                      <td style={{ padding: '11px 14px', fontSize: 12, color: 'var(--off-white)', borderBottom: '1px solid rgba(255,255,255,.04)' }}>{u.timezone || '—'}</td>
                      <td style={{ padding: '11px 14px', fontSize: 12, color: 'var(--gray)', borderBottom: '1px solid rgba(255,255,255,.04)', whiteSpace: 'nowrap' }}>{fmtDate(u.joinedAt)}</td>
                      <td style={{ padding: '11px 14px', borderBottom: '1px solid rgba(255,255,255,.04)' }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={() => setDetailId(u.id)} style={{ padding: '4px 10px', background: 'rgba(212,168,67,.08)', border: '1px solid rgba(212,168,67,.2)', color: 'var(--gold)', fontSize: 10, fontWeight: 700, letterSpacing: '.04em', cursor: 'pointer', fontFamily: "'Inter',sans-serif", borderRadius: 4 }}>VIEW</button>
                          <button onClick={e => quickSuspend(e, u)} style={{ padding: '4px 10px', background: u.suspended ? 'rgba(46,204,138,.08)' : 'rgba(251,191,36,.08)', border: `1px solid ${u.suspended ? 'rgba(46,204,138,.25)' : 'rgba(251,191,36,.25)'}`, color: u.suspended ? 'var(--green)' : 'var(--warning)', fontSize: 10, fontWeight: 700, letterSpacing: '.04em', cursor: 'pointer', fontFamily: "'Inter',sans-serif", borderRadius: 4 }}>
                            {u.suspended ? 'RESTORE' : 'SUSPEND'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!data.users.length && (
                    <tr><td colSpan={7} style={{ padding: 40, textAlign: 'center', color: 'var(--gray)', fontSize: 13 }}>No users found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile card list */}
            <div className="users-card-list" style={{ flexDirection: 'column', gap: 8 }}>
              {data.users.map((u, i) => (
                <UserCard
                  key={u.id} u={u} i={i}
                  onView={id => setDetailId(id)}
                  onQuickSuspend={quickSuspend}
                />
              ))}
              {!data.users.length && (
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray)', fontSize: 13 }}>No users found.</div>
              )}
            </div>
          </>
        )}

        {/* Pagination */}
        {data.pages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 8, flexWrap: 'wrap', gap: 8 }}>
            <span style={{ fontSize: 11, color: 'var(--gray)' }}>Showing {data.users.length} of {data.total} users</span>
            <div style={{ display: 'flex', gap: 4 }}>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ padding: '5px 12px', background: 'var(--dark3)', border: '1px solid rgba(255,255,255,.08)', color: 'var(--gray)', fontSize: 11, cursor: page === 1 ? 'not-allowed' : 'pointer', opacity: page === 1 ? .4 : 1, borderRadius: 4, fontFamily: "'Inter',sans-serif" }}>← Prev</button>
              <span style={{ padding: '5px 12px', fontSize: 11, color: 'var(--off-white)' }}>Page {page} of {data.pages}</span>
              <button onClick={() => setPage(p => Math.min(data.pages, p + 1))} disabled={page === data.pages} style={{ padding: '5px 12px', background: 'var(--dark3)', border: '1px solid rgba(255,255,255,.08)', color: 'var(--gray)', fontSize: 11, cursor: page === data.pages ? 'not-allowed' : 'pointer', opacity: page === data.pages ? .4 : 1, borderRadius: 4, fontFamily: "'Inter',sans-serif" }}>Next →</button>
            </div>
          </div>
        )}

        {detailId && (
          <UserDetailModal
            userId={detailId}
            onClose={() => setDetailId(null)}
            onSuspendToggle={handleSuspendToggle}
            onDelete={handleDelete}
            onRoleChange={handleRoleChange}
          />
        )}
        {confirm && <ConfirmDialog {...confirm} onCancel={() => setConfirm(null)}/>}
      </div>
    </>
  );
}